/**
 * hud.js — wallet, prompts, toasts, score popups and the payout summary.
 *
 * Two presentations of the same state:
 *   - DOM overlay, used on desktop (elements already in index.html)
 *   - an in-world panel that floats near the player, used in VR where DOM
 *     overlays are invisible
 *
 * The HUD only listens to the bus. Nothing calls into it directly, so a stall
 * gets a score popup by emitting an event, not by importing this module.
 *
 * @module ui/hud
 */

import * as THREE from 'three';
import { EVENTS } from '../constants.js';
import { bus } from './../core/events.js';
import { economy } from '../core/save.js';
import { createPanel, createScorePopup } from './panels.js';

class HUD {
  constructor() {
    /** @type {THREE.Object3D} */ this.root = null;
    /** @type {THREE.Mesh} */ this.walletPanel = null;
    /** @type {THREE.Camera} */ this.camera = null;
    /** @type {THREE.Scene} */ this.scene = null;
    /** @type {Array<THREE.Sprite>} */ this._popups = [];
    /** @type {Record<string, HTMLElement>} */ this.el = {};
    this._toastTimers = new Set();
    this._payoutHandlers = { again: null, exit: null };
    this.inVR = false;
  }

  /**
   * @param {THREE.Scene} scene
   * @param {THREE.Camera} camera
   */
  init(scene, camera) {
    this.scene = scene;
    this.camera = camera;

    this.el = {
      hud: document.getElementById('cc-hud'),
      tickets: document.getElementById('cc-tickets'),
      tokens: document.getElementById('cc-tokens'),
      stars: document.getElementById('cc-stars'),
      stallHud: document.getElementById('cc-stallhud'),
      stallName: document.getElementById('cc-stall-name'),
      stallScore: document.getElementById('cc-stall-score'),
      stallSub: document.getElementById('cc-stall-sub'),
      prompt: document.getElementById('cc-prompt'),
      toast: document.getElementById('cc-toast'),
      crosshair: document.getElementById('cc-crosshair'),
      payout: document.getElementById('cc-payout'),
      payoutScore: document.getElementById('cc-payout-score'),
      payoutRows: document.getElementById('cc-payout-rows'),
      payoutAgain: document.getElementById('cc-payout-again'),
      payoutExit: document.getElementById('cc-payout-exit'),
    };
    if (this.el.hud) this.el.hud.classList.add('on');
    if (this.el.crosshair) this.el.crosshair.classList.add('on');

    this.el.payoutAgain?.addEventListener('click', () => {
      this.hidePayout();
      this._payoutHandlers.again?.();
    });
    this.el.payoutExit?.addEventListener('click', () => {
      this.hidePayout();
      this._payoutHandlers.exit?.();
    });

    // In-world wallet, parented to the camera so it stays legible in VR.
    this.root = new THREE.Group();
    this.root.name = 'hud-root';
    this.walletPanel = createPanel({
      title: 'WALLET', width: 0.34, height: 0.2,
      lines: ['🎟 0', '🪙 0', '⭐ 0'], align: 'left',
    });
    this.walletPanel.position.set(-0.34, 0.2, -0.75);
    this.walletPanel.rotation.y = 0.36;
    this.walletPanel.visible = false;
    camera.add(this.walletPanel);

    this._bindEvents();
    this.refreshWallet();
    return this;
  }

  /** @private */
  _bindEvents() {
    bus.on(EVENTS.WALLET_CHANGE, () => this.refreshWallet());
    bus.on(EVENTS.SAVE_LOAD, () => this.refreshWallet());

    bus.on(EVENTS.UI_PROMPT, ({ text, show = true } = {}) => {
      if (show && text) this.showPrompt(text);
      else this.hidePrompt();
    });

    bus.on(EVENTS.UI_TOAST, ({ text, kind = 'good', duration = 2400 } = {}) => {
      this.toast(text, kind, duration);
    });

    bus.on(EVENTS.UI_SCORE_POPUP, ({ text, position, color } = {}) => {
      this.scorePopup(text, position, { color });
    });

    bus.on(EVENTS.STALL_SCORE, ({ total, attemptsLeft, name } = {}) => {
      this.setStallScore(total, attemptsLeft, name);
    });

    bus.on(EVENTS.DIRECT_PRIZE, (prize) => {
      const label = prize.kind === 'fish'
        ? `You won a ${prize.variant || 'fish'}!`
        : `You won a ${prize.kind === 'plush-large' ? 'LARGE' : 'small'} plush!`;
      this.toast(label, 'prize', 3200);
    });

    bus.on(EVENTS.INSUFFICIENT_TICKETS, ({ needed, have }) => {
      this.toast(`Need ${needed} tickets — you have ${have}`, 'bad', 2600);
    });

    bus.on(EVENTS.XR_SESSION_START, () => this.setVRMode(true));
    bus.on(EVENTS.XR_SESSION_END, () => this.setVRMode(false));
  }

  /** Swap between DOM overlay and in-world panels. */
  setVRMode(on) {
    this.inVR = on;
    if (this.el.hud) this.el.hud.classList.toggle('on', !on);
    if (this.walletPanel) this.walletPanel.visible = on;
    this.refreshWallet();
  }

  refreshWallet() {
    const w = economy.wallet();
    if (this.el.tickets) this.el.tickets.textContent = String(w.tickets);
    if (this.el.tokens) this.el.tokens.textContent = String(w.tokens);
    if (this.el.stars) this.el.stars.textContent = String(w.stars);
    if (this.walletPanel && this.walletPanel.visible) {
      this.walletPanel.setLines([`Tickets  ${w.tickets}`, `Tokens   ${w.tokens}`, `Stars    ${w.stars}`]);
    }
  }

  // ── Prompts & toasts ──────────────────────────────────────────────────────

  /** @param {string} text May contain `<kbd>` markup. */
  showPrompt(text) {
    if (!this.el.prompt) return;
    this.el.prompt.innerHTML = text;
    this.el.prompt.classList.add('on');
  }

  hidePrompt() {
    this.el.prompt?.classList.remove('on');
  }

  /**
   * @param {string} text
   * @param {'good'|'bad'|'prize'} [kind]
   * @param {number} [duration] ms
   */
  toast(text, kind = 'good', duration = 2400) {
    if (!this.el.toast) return;
    const div = document.createElement('div');
    div.className = `t ${kind}`;
    div.textContent = text;
    this.el.toast.appendChild(div);
    const timer = setTimeout(() => {
      div.remove();
      this._toastTimers.delete(timer);
    }, duration);
    this._toastTimers.add(timer);
  }

  // ── Stall readout ─────────────────────────────────────────────────────────

  /**
   * @param {string} name
   * @param {string} [sub]
   */
  showStallHud(name, sub = '') {
    if (!this.el.stallHud) return;
    this.el.stallHud.classList.add('on');
    if (this.el.stallName) this.el.stallName.textContent = name;
    if (this.el.stallScore) this.el.stallScore.textContent = '0';
    if (this.el.stallSub) this.el.stallSub.textContent = sub;
  }

  hideStallHud() {
    this.el.stallHud?.classList.remove('on');
  }

  /**
   * @param {number} total
   * @param {number} [attemptsLeft]
   * @param {string} [name]
   */
  setStallScore(total, attemptsLeft, name) {
    if (this.el.stallScore && total !== undefined) {
      this.el.stallScore.textContent = String(total);
    }
    if (this.el.stallSub && attemptsLeft !== undefined && attemptsLeft !== null) {
      this.el.stallSub.textContent = `${attemptsLeft} left`;
    }
    if (name && this.el.stallName) this.el.stallName.textContent = name;
  }

  // ── Payout ────────────────────────────────────────────────────────────────

  /**
   * Show the round summary (GDD 6.1 "Ticket Payout").
   * @param {{ score: number, tokens: number, prizes?: string[], stallName?: string }} result
   * @param {{ onAgain?: Function, onExit?: Function, canAfford?: boolean, cost?: number }} [handlers]
   */
  showPayout(result, handlers = {}) {
    this._payoutHandlers.again = handlers.onAgain || null;
    this._payoutHandlers.exit = handlers.onExit || null;

    if (this.el.payoutScore) this.el.payoutScore.textContent = String(result.score ?? 0);
    if (this.el.payoutRows) {
      const rows = [`<div><span>Redemption tokens</span><b>+${result.tokens ?? 0}</b></div>`];
      for (const p of result.prizes || []) {
        rows.push(`<div><span>Prize won</span><b>${p}</b></div>`);
      }
      this.el.payoutRows.innerHTML = rows.join('');
    }
    if (this.el.payoutAgain) {
      const affordable = handlers.canAfford !== false;
      this.el.payoutAgain.disabled = !affordable;
      this.el.payoutAgain.textContent = affordable
        ? `Play Again (${handlers.cost ?? '?'} 🎟)`
        : 'Not enough tickets';
      this.el.payoutAgain.style.opacity = affordable ? '1' : '0.5';
    }
    this.el.payout?.classList.add('on');
    bus.emit(EVENTS.UI_PAYOUT, { shown: true, ...result });
  }

  hidePayout() {
    this.el.payout?.classList.remove('on');
  }

  // ── Score popups ──────────────────────────────────────────────────────────

  /**
   * Spawn a rising score number in world space.
   * @param {string|number} text
   * @param {THREE.Vector3|{x,y,z}} position
   * @param {{ color?: string, size?: number }} [opts]
   */
  scorePopup(text, position, opts = {}) {
    if (!this.scene || !position) return;
    const sprite = createScorePopup(text, opts);
    const holder = new THREE.Object3D();
    holder.position.set(position.x, position.y, position.z);
    holder.add(sprite);
    this.scene.add(holder);
    this._popups.push({ holder, sprite });
  }

  update(dt) {
    for (let i = this._popups.length - 1; i >= 0; i--) {
      const { holder, sprite } = this._popups[i];
      if (!sprite.userData.update(dt)) {
        this.scene.remove(holder);
        this._popups.splice(i, 1);
      }
    }
  }

  /** Drop every transient element. */
  clear() {
    for (const { holder } of this._popups) this.scene.remove(holder);
    this._popups.length = 0;
    for (const t of this._toastTimers) clearTimeout(t);
    this._toastTimers.clear();
    if (this.el.toast) this.el.toast.innerHTML = '';
    this.hidePrompt();
    this.hidePayout();
    this.hideStallHud();
  }
}

/** The one HUD. */
export const hud = new HUD();
